import {
  CANADIAN_PROVINCE_NAMES,
  CANADIAN_PROVINCES,
  FULL_PATIENT_INTAKE_SECTIONS,
  type LockedBookingIdentity,
  normalizeOntarioHealthNumber,
  OtherCanadianProvinceSchema,
  type PatientIntakeAnswers,
  type PatientIntakeAnswersFromDraftOptions,
  type PatientIntakeSections,
  type PatientQuestionnaireCoverageChoice,
  type PublicConsentRequirement,
  type PublicIntakeConsent,
  type PublicVisitKind,
  patientIntakeAnswersFromDraft,
  patientIntakeAsksIdentity,
} from "@emr/contracts";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  cloneElement,
  type ReactNode,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { ConsentAgreement } from "./consent/ConsentAgreement";
import {
  type ConsentChoices,
  initialConsentChoices,
  intakeConsent,
  termsAgreed,
  termsRequiredToSubmit,
} from "./consent/consentChoices";
import { Icon, type IconName } from "./Icon";
import {
  type HealthCardPhotoControl,
  HealthCardPhotoRow,
  type HealthCardReadingApplied,
  type HealthCardReadingOptions,
  type SendHealthCardPhoto,
  useHealthCardPhoto,
} from "./intake/HealthCardPhoto";
import { PatientTopBar, StateMark } from "./intake/PatientChrome";
import { type HealthCardReading, healthCardFill } from "./lib/healthCardReader";
import { sexLabel, sexOptions } from "./lib/patientSex";
import {
  answersNotAccepted,
  isSchemaError,
  type QuestionnaireStepKey,
  stepsFor,
  submissionErrors,
  validateStep,
} from "./lib/validation";
import { RequiredMarker } from "./RequiredMarker";
import { StepProgress } from "./StepProgress";
import type { IntakeData } from "./types";

const symptomOptions = [
  "Fever",
  "Cough",
  "Sore throat",
  "Shortness of breath",
  "Pain",
  "Nausea",
  "Rash",
  "Fatigue",
];

const pregnancyOptions: ReadonlyArray<{
  readonly value: IntakeData["pregnant"];
  readonly label: string;
}> = [
  { value: "not-applicable", label: "Not applicable" },
  { value: "no", label: "No" },
  { value: "yes", label: "Yes or possibly" },
  { value: "prefer-not-to-say", label: "Prefer to discuss privately" },
];

export type SessionEndReason = "idle" | "manual" | "restored";

/** Why a questionnaire closed, for this screen and for a host that replaces it with its own. */
export function sessionEndMessage(reason: SessionEndReason): string {
  if (reason === "idle")
    return "Closed after inactivity, for your privacy. Your answers were cleared from this device.";
  if (reason === "restored")
    return "For your privacy, a restored browser page cannot reopen the questionnaire. Your answers were cleared from this device.";
  return "Your answers were cleared from this device.";
}

/** To the top of the page after a step change or an error, without motion when that is asked. */
function scrollToTop() {
  const reduceMotion =
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  window.scrollTo({ top: 0, behavior: reduceMotion ? "auto" : "smooth" });
}

/** The quiet way back, above the step's title, named for the step it goes to. */
const backLabels: Record<QuestionnaireStepKey, string> = {
  identity: "Back to about you",
  coverage: "Back to coverage",
  health: "Back to health",
  review: "Back to review",
};

export interface ScheduledIntakeContext {
  readonly clinicName: string;
  readonly clinicAddress: string;
  readonly providerDisplayName: string;
  readonly appointmentTypeName: string;
  readonly startAt: string;
  readonly endAt: string;
}

export interface IntakeSubmissionResult {
  readonly receipt: string;
  readonly notice?: string;
}

interface AppProps {
  readonly idleTimeoutMs?: number;
  /**
   * The answers the questionnaire starts from. Its date of birth is the one the appointment
   * holds (verified to open the link, or given at booking): the server refuses any other, so it
   * is shown read-only.
   */
  readonly initialIntake: IntakeData;
  /**
   * The sex, mobile number and email the patient typed when booking online, as
   * lockedBookingIdentity returns them: the questionnaire shows them read-only and submits them
   * unchanged. Sex "unknown" is never locked; the patient answers it here.
   */
  readonly lockedIdentity?: LockedBookingIdentity;
  readonly scheduledContext: ScheduledIntakeContext;
  /**
   * What the Review step asks the patient to agree to (a clinic link's session). Absent in the
   * demo lane; both documents are null when the appointment already has the patient's answers.
   */
  readonly consentDocuments?: PublicConsentRequirement;
  /**
   * The sections the link asks for (the session's questionnaireSections), every one by default.
   * Without identity the questionnaire starts at Coverage, shows no About you step or review
   * section, and submits answers with no identity key at all (PA-5).
   */
  readonly questionnaireSections?: PatientIntakeSections;
  /**
   * A walk-in's link (PA-5): the patient is already at the clinic, so the questionnaire names no
   * appointment time or clinician and says their answers go to the front desk.
   */
  readonly visitKind?: PublicVisitKind;
  /**
   * The patient is already at the clinic (a walk-in, or a booking the desk checked in), so the
   * questionnaire does not tell them to check in when they arrive. True for every walk-in.
   */
  readonly atClinic?: boolean;
  /**
   * Receives the validated answers, compacted from the form by the shared contract for the asked
   * sections only, and an answer to each document consentDocuments asked about (undefined when
   * it asked about none).
   */
  readonly submitIntake: (
    answers: PatientIntakeAnswers,
    consent: PublicIntakeConsent | undefined,
  ) => Promise<IntakeSubmissionResult>;
  readonly persisted?: boolean;
  readonly onSessionEnd?: (reason: SessionEndReason) => void;
  /**
   * Sends a photo of the patient's health card (PA-13): a clinic link's session only, never the
   * demo lane. With it, the coverage step offers an optional photo for an Ontario or another
   * province's card when the browser can prepare one. The photo goes in the background: Continue
   * and Submit never wait for it, and the card number stays required.
   */
  readonly healthCardPhoto?: SendHealthCardPhoto;
}

type UpdateIntake = <K extends keyof IntakeData>(key: K, next: IntakeData[K]) => void;

/** The answers an appointment can fix. Each one it fixes is shown read-only with its note. */
type LockedField = "dateOfBirth" | "administrativeSex" | "phone" | "email";
type LockedFields = Partial<Record<LockedField, string>>;

const bookingIdentityNote = "From your booking. Wrong? Tell the front desk.";

/** The booking values the questionnaire locks: never sex "unknown", which is asked again. */
function lockedIdentityValues(
  identity: LockedBookingIdentity | undefined,
): Partial<Pick<IntakeData, "administrativeSex" | "phone" | "email">> {
  if (identity === undefined) return {};
  const { administrativeSex, phone, email } = identity;
  return {
    ...(administrativeSex === undefined || administrativeSex === "unknown"
      ? {}
      : { administrativeSex }),
    ...(phone === undefined ? {} : { phone }),
    ...(email === undefined ? {} : { email }),
  };
}

const coverageChoices: ReadonlyArray<{
  readonly value: PatientQuestionnaireCoverageChoice;
  readonly title: string;
  readonly icon: IconName;
}> = [
  { value: "ohip", title: "Ontario health card (OHIP)", icon: "card" },
  { value: "other_province", title: "Another province or territory", icon: "location" },
  { value: "ifh", title: "Interim Federal Health Program (IFH)", icon: "document" },
  { value: "none", title: "No provincial or federal coverage", icon: "info" },
];

const notGiven = "Not given — the clinic will confirm";

function coverageLabel(intake: IntakeData): string {
  if (intake.coverageChoice === "other_province" && intake.otherPlanProvince !== "")
    return `${CANADIAN_PROVINCE_NAMES[intake.otherPlanProvince]} health card`;
  return (
    coverageChoices.find((choice) => choice.value === intake.coverageChoice)?.title ?? "Not chosen"
  );
}

/** Only the last four characters of a card or client number are ever shown back. */
function maskedNumber(value: string): string {
  return `••••${normalizeOntarioHealthNumber(value).slice(-4)}`;
}

function joinedText(parts: readonly string[], separator: string): string {
  return parts
    .map((part) => part.trim())
    .filter(Boolean)
    .join(separator);
}

function formattedAddress(intake: IntakeData): string {
  const local = joinedText([intake.addressLine1, intake.addressLine2, intake.city], ", ");
  if (local === "" && intake.postalCode.trim() === "") return "Not provided";
  const region = joinedText(
    [CANADIAN_PROVINCE_NAMES[intake.province], intake.postalCode.toUpperCase()],
    " ",
  );
  return joinedText([local, region], ", ");
}

function formattedEmergencyContact(intake: IntakeData): string {
  const name = intake.emergencyContactName.trim();
  const relationship = intake.emergencyContactRelationship.trim();
  const contact = joinedText(
    [relationship === "" ? name : `${name} (${relationship})`, intake.emergencyContactPhone],
    " · ",
  );
  return contact === "" ? "Not provided" : contact;
}

function Field({
  label,
  required,
  hint,
  wide,
  children,
}: {
  label: string;
  required?: boolean;
  hint?: string;
  /** Spans every column of its grid. */
  wide?: boolean;
  children: React.ReactElement<{ id?: string; required?: boolean; "aria-describedby"?: string }>;
}) {
  const generatedId = useId();
  const fieldId = `field-${generatedId.replaceAll(":", "")}`;
  const hintId = `${fieldId}-hint`;
  // The form has noValidate, so native `required` only tells assistive technology; the step's
  // own validation still decides what blocks Continue.
  const control = cloneElement(children, {
    id: fieldId,
    ...(required ? { required: true } : {}),
    ...(hint ? { "aria-describedby": hintId } : {}),
  });
  return (
    <div className={wide ? "field field-wide" : "field"}>
      <label htmlFor={fieldId}>
        {label}
        {required ? <RequiredMarker /> : null}
      </label>
      {control}
      {hint ? <small id={hintId}>{hint}</small> : null}
    </div>
  );
}

/**
 * A short one-of question as a row of tappable chips (radio buttons in a fieldset), in place of
 * a list to open. A required group is marked once, in its legend.
 */
function ChoiceChips<T extends string>({
  legend,
  value,
  options,
  onChange,
  required = false,
  twoColumns = false,
}: {
  legend: string;
  value: T | "";
  options: ReadonlyArray<{ readonly value: T; readonly label: string }>;
  onChange: (value: T) => void;
  required?: boolean;
  /** Two chips a row, for a group that shares its grid row with another field. */
  twoColumns?: boolean;
}) {
  const name = `choice-${useId().replaceAll(":", "")}`;
  return (
    <fieldset className={twoColumns ? "choice-chips two" : "choice-chips"}>
      <legend>
        {legend}
        {required ? <RequiredMarker /> : null}
      </legend>
      <div>
        {options.map((option) => {
          const checked = value === option.value;
          return (
            <label key={option.value} className={checked ? "selected" : undefined}>
              <input
                type="radio"
                name={name}
                value={option.value}
                checked={checked}
                required={required}
                onChange={() => onChange(option.value)}
              />
              {checked ? <Icon name="check" /> : null}
              <span>{option.label}</span>
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}

function StepHeader({ title, hint }: { title: string; hint?: string }) {
  return (
    <header className="step-header">
      <h1 id="intake-step-heading" tabIndex={-1}>
        {title}
      </h1>
      {hint ? <p>{hint}</p> : null}
    </header>
  );
}

function AboutStep({
  value,
  update,
  locked,
}: {
  value: IntakeData;
  update: UpdateIntake;
  /**
   * The answers the appointment fixes, each with the note shown under it: read-only, and not
   * marked required because the patient has nothing to enter.
   */
  locked: LockedFields;
}) {
  return (
    <>
      <StepHeader title="Tell us about you" hint="Use your legal name, as on your health card." />
      <div className="form-grid two">
        <Field label="Legal first name" required>
          <input
            autoComplete="off"
            maxLength={100}
            value={value.legalFirstName}
            onChange={(event) => update("legalFirstName", event.target.value)}
          />
        </Field>
        <Field label="Legal last name" required>
          <input
            autoComplete="off"
            maxLength={100}
            value={value.legalLastName}
            onChange={(event) => update("legalLastName", event.target.value)}
          />
        </Field>
        <Field label="Legal middle name">
          <input
            autoComplete="off"
            maxLength={100}
            value={value.legalMiddleName}
            onChange={(event) => update("legalMiddleName", event.target.value)}
          />
        </Field>
        <Field label="Preferred name">
          <input
            autoComplete="off"
            maxLength={120}
            value={value.preferredName}
            onChange={(event) => update("preferredName", event.target.value)}
          />
        </Field>
        {locked.dateOfBirth === undefined ? (
          <Field label="Date of birth" required>
            <input
              type="date"
              autoComplete="off"
              value={value.dateOfBirth}
              onChange={(event) => update("dateOfBirth", event.target.value)}
            />
          </Field>
        ) : (
          <Field label="Date of birth" hint={locked.dateOfBirth}>
            <input type="date" autoComplete="off" readOnly value={value.dateOfBirth} />
          </Field>
        )}
        {locked.administrativeSex === undefined ? (
          <ChoiceChips
            legend="Sex"
            required
            twoColumns
            value={value.administrativeSex}
            options={sexOptions}
            onChange={(next) => update("administrativeSex", next)}
          />
        ) : (
          <Field label="Sex" hint={locked.administrativeSex}>
            <input autoComplete="off" readOnly value={sexLabel(value.administrativeSex)} />
          </Field>
        )}
        {locked.phone === undefined ? (
          <Field label="Mobile number" required hint="For messages about your visit.">
            <input
              type="tel"
              autoComplete="off"
              inputMode="tel"
              maxLength={40}
              value={value.phone}
              onChange={(event) => update("phone", event.target.value)}
            />
          </Field>
        ) : (
          <Field label="Mobile number" hint={locked.phone}>
            <input type="tel" autoComplete="off" readOnly value={value.phone} />
          </Field>
        )}
        {locked.email === undefined ? (
          <Field label="Email">
            <input
              type="email"
              autoComplete="off"
              inputMode="email"
              maxLength={254}
              value={value.email}
              onChange={(event) => update("email", event.target.value)}
            />
          </Field>
        ) : (
          <Field label="Email" hint={locked.email}>
            <input type="email" autoComplete="off" readOnly value={value.email} />
          </Field>
        )}
      </div>
      <h2 className="form-section-title">Home address</h2>
      <div className="form-grid two">
        <Field label="Address line 1" wide>
          <input
            autoComplete="off"
            maxLength={200}
            value={value.addressLine1}
            onChange={(event) => update("addressLine1", event.target.value)}
          />
        </Field>
        <Field label="Address line 2" hint="Apartment, unit or suite">
          <input
            autoComplete="off"
            maxLength={200}
            value={value.addressLine2}
            onChange={(event) => update("addressLine2", event.target.value)}
          />
        </Field>
        <Field label="City">
          <input
            autoComplete="off"
            maxLength={120}
            value={value.city}
            onChange={(event) => update("city", event.target.value)}
          />
        </Field>
        <Field label="Province or territory">
          <select
            value={value.province}
            onChange={(event) => update("province", event.target.value as IntakeData["province"])}
          >
            {CANADIAN_PROVINCES.map((province) => (
              <option key={province} value={province}>
                {CANADIAN_PROVINCE_NAMES[province]}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Postal code">
          <input
            autoComplete="off"
            autoCapitalize="characters"
            maxLength={7}
            value={value.postalCode}
            onChange={(event) => update("postalCode", event.target.value.toUpperCase())}
          />
        </Field>
      </div>
      <h2 className="form-section-title">
        Emergency contact <span>Optional</span>
      </h2>
      <p className="form-section-note">If you add someone, give their name and phone number.</p>
      <div className="form-grid two">
        <Field label="Contact name">
          <input
            autoComplete="off"
            maxLength={200}
            value={value.emergencyContactName}
            onChange={(event) => update("emergencyContactName", event.target.value)}
          />
        </Field>
        <Field label="Relationship to you">
          <input
            autoComplete="off"
            maxLength={80}
            value={value.emergencyContactRelationship}
            onChange={(event) => update("emergencyContactRelationship", event.target.value)}
          />
        </Field>
        <Field label="Contact phone">
          <input
            type="tel"
            autoComplete="off"
            inputMode="tel"
            maxLength={40}
            value={value.emergencyContactPhone}
            onChange={(event) => update("emergencyContactPhone", event.target.value)}
          />
        </Field>
      </div>
    </>
  );
}

function CoverageStep({
  value,
  update,
  photo,
}: {
  value: IntakeData;
  update: UpdateIntake;
  /** The optional card photo; absent when this session or browser cannot send one. */
  photo: HealthCardPhotoControl | undefined;
}) {
  return (
    <>
      <StepHeader title="Your health coverage" />
      <fieldset className="choice-cards">
        <legend>
          Which health coverage do you have?
          <RequiredMarker />
        </legend>
        <div>
          {coverageChoices.map((choice) => (
            <label
              key={choice.value}
              className={value.coverageChoice === choice.value ? "selected" : ""}
            >
              <input
                type="radio"
                name="coverage-choice"
                required
                checked={value.coverageChoice === choice.value}
                onChange={() => update("coverageChoice", choice.value)}
              />
              <span className="choice-card-mark">
                <Icon name={choice.icon} />
              </span>
              <strong>{choice.title}</strong>
              <i>
                <Icon name="check" />
              </i>
            </label>
          ))}
        </div>
      </fieldset>
      {photo !== undefined &&
      (value.coverageChoice === "ohip" || value.coverageChoice === "other_province") ? (
        <HealthCardPhotoRow control={photo} />
      ) : null}
      {value.coverageChoice === "ohip" ? (
        <div className="form-grid two">
          <Field
            label="Health card number"
            required
            hint="The 10 digits on the front of your card."
            wide
          >
            <input
              autoComplete="off"
              inputMode="numeric"
              maxLength={14}
              value={value.healthNumber}
              onChange={(event) => update("healthNumber", event.target.value)}
            />
          </Field>
          <Field label="Version code" hint="The letters after the number. Not sure? Leave blank.">
            <input
              autoComplete="off"
              autoCapitalize="characters"
              maxLength={2}
              value={value.versionCode}
              onChange={(event) => update("versionCode", event.target.value.toUpperCase())}
            />
          </Field>
          <Field label="Expiry date" hint="Red-and-white cards have no expiry: leave blank.">
            <input
              type="date"
              autoComplete="off"
              value={value.expiryDate}
              onChange={(event) => update("expiryDate", event.target.value)}
            />
          </Field>
        </div>
      ) : null}
      {value.coverageChoice === "other_province" ? (
        <div className="form-grid two">
          <Field label="Province or territory of your card" required>
            <select
              value={value.otherPlanProvince}
              onChange={(event) =>
                update("otherPlanProvince", event.target.value as IntakeData["otherPlanProvince"])
              }
            >
              <option value="">Choose one</option>
              {OtherCanadianProvinceSchema.options.map((province) => (
                <option key={province} value={province}>
                  {CANADIAN_PROVINCE_NAMES[province]}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Health card number" required>
            <input
              autoComplete="off"
              maxLength={120}
              value={value.otherPlanNumber}
              onChange={(event) => update("otherPlanNumber", event.target.value)}
            />
          </Field>
        </div>
      ) : null}
      {value.coverageChoice === "ifh" ? (
        <div className="form-grid two">
          <Field label="IFH client ID (UCI)" required hint="Printed on your IFH certificate.">
            <input
              autoComplete="off"
              maxLength={120}
              value={value.ifhNumber}
              onChange={(event) => update("ifhNumber", event.target.value)}
            />
          </Field>
        </div>
      ) : null}
      {value.coverageChoice === "none" ? (
        <p className="coverage-note">Staff will go over your coverage at your visit.</p>
      ) : null}
    </>
  );
}

function HealthStep({ value, update }: { value: IntakeData; update: UpdateIntake }) {
  const toggleSymptom = (symptom: string) =>
    update(
      "symptoms",
      value.symptoms.includes(symptom)
        ? value.symptoms.filter((item) => item !== symptom)
        : [...value.symptoms, symptom],
    );
  return (
    <>
      <StepHeader title="What brings you in?" />
      <Field
        label="Main reason for your visit"
        required
        hint="When it started, and what makes it better or worse."
      >
        <textarea
          rows={3}
          value={value.reason}
          onChange={(event) => update("reason", event.target.value)}
        />
      </Field>
      <fieldset className="symptom-field">
        <legend>
          Symptoms <span>Choose any</span>
        </legend>
        <div>
          {symptomOptions.map((symptom) => (
            <button
              type="button"
              aria-pressed={value.symptoms.includes(symptom)}
              className={value.symptoms.includes(symptom) ? "selected" : ""}
              key={symptom}
              onClick={() => toggleSymptom(symptom)}
            >
              {value.symptoms.includes(symptom) ? <Icon name="check" /> : null}
              {symptom}
            </button>
          ))}
        </div>
      </fieldset>
      <div className="form-grid two">
        <Field label="Allergies" hint="Write “none known” if you have none.">
          <textarea
            rows={2}
            value={value.allergies}
            onChange={(event) => update("allergies", event.target.value)}
          />
        </Field>
        <Field label="Current medications">
          <textarea
            rows={2}
            value={value.medications}
            onChange={(event) => update("medications", event.target.value)}
          />
        </Field>
        <Field label="Important medical history" wide>
          <textarea
            rows={2}
            value={value.medicalHistory}
            onChange={(event) => update("medicalHistory", event.target.value)}
          />
        </Field>
      </div>
      <ChoiceChips
        legend="Could you be pregnant?"
        value={value.pregnant}
        options={pregnancyOptions}
        onChange={(next) => update("pregnant", next)}
      />
    </>
  );
}

/** One section of the review: its name, Edit, and what was answered. */
function ReviewSection({
  title,
  edit,
  children,
}: {
  title: string;
  edit: () => void;
  children: ReactNode;
}) {
  return (
    <section>
      <header>
        <strong>{title}</strong>
        <button type="button" onClick={edit}>
          Edit <span className="sr-only">{title.toLowerCase()}</span>
        </button>
      </header>
      <dl>{children}</dl>
    </section>
  );
}

function ReviewStep({
  value,
  asksIdentity,
  atClinic,
  edit,
  agreement,
}: {
  value: IntakeData;
  /** Whether the link asked About you; a returning walk-in's review has no such section. */
  asksIdentity: boolean;
  /** The patient is already at the clinic: the answers go to the front desk, not to a check-in. */
  atClinic: boolean;
  edit: (step: QuestionnaireStepKey) => void;
  /** The terms agreement, when the appointment does not have one yet. */
  agreement: ReactNode;
}) {
  // Without About you the questionnaire never asked a name, so the heading uses none.
  const displayName = asksIdentity ? value.preferredName || value.legalFirstName : "";
  return (
    <>
      <StepHeader
        title={
          displayName === ""
            ? "Ready to submit your questionnaire?"
            : `Ready to submit your questionnaire, ${displayName}?`
        }
        hint="Check your answers, then submit."
      />
      <div className="review-sections">
        {asksIdentity ? <IdentityReview value={value} edit={() => edit("identity")} /> : null}
        <ReviewSection title="Health coverage" edit={() => edit("coverage")}>
          <div className="wide">
            <dt>Coverage</dt>
            <dd>{coverageLabel(value)}</dd>
          </div>
          {value.coverageChoice === "ohip" ? (
            <>
              <div>
                <dt>Health card number</dt>
                <dd>{maskedNumber(value.healthNumber)}</dd>
              </div>
              <div>
                <dt>Version code</dt>
                <dd>{value.versionCode.trim() || notGiven}</dd>
              </div>
              <div>
                <dt>Expiry date</dt>
                <dd>{value.expiryDate || notGiven}</dd>
              </div>
            </>
          ) : null}
          {value.coverageChoice === "other_province" ? (
            <div>
              <dt>Health card number</dt>
              <dd>{maskedNumber(value.otherPlanNumber)}</dd>
            </div>
          ) : null}
          {value.coverageChoice === "ifh" ? (
            <div>
              <dt>IFH client ID (UCI)</dt>
              <dd>{maskedNumber(value.ifhNumber)}</dd>
            </div>
          ) : null}
        </ReviewSection>
        <ReviewSection title="Health" edit={() => edit("health")}>
          <div className="wide">
            <dt>Visit reason</dt>
            <dd>{value.reason}</dd>
          </div>
          <div className="wide">
            <dt>Allergies</dt>
            <dd>{value.allergies || "None provided"}</dd>
          </div>
        </ReviewSection>
      </div>
      {agreement}
      <p className="submit-notice">
        <Icon name="lock" />
        {atClinic
          ? "Submitting sends your answers to the front desk. Staff review them before your chart changes."
          : "Staff review your answers before your chart changes. You still check in when you arrive."}
      </p>
    </>
  );
}

/** The review's About you section, shown only when the link asked for it. */
function IdentityReview({ value, edit }: { value: IntakeData; edit: () => void }) {
  return (
    <ReviewSection title="About you" edit={edit}>
      <div>
        <dt>Legal name</dt>
        <dd>
          {joinedText([value.legalFirstName, value.legalMiddleName, value.legalLastName], " ")}
        </dd>
      </div>
      <div>
        <dt>Date of birth</dt>
        <dd>{value.dateOfBirth}</dd>
      </div>
      <div>
        <dt>Sex</dt>
        <dd>{sexLabel(value.administrativeSex)}</dd>
      </div>
      <div>
        <dt>Mobile</dt>
        <dd>{value.phone}</dd>
      </div>
      <div>
        <dt>Email</dt>
        <dd>{value.email.trim() || "Not provided"}</dd>
      </div>
      <div>
        <dt>Home address</dt>
        <dd>{formattedAddress(value)}</dd>
      </div>
      <div className="wide">
        <dt>Emergency contact</dt>
        <dd>{formattedEmergencyContact(value)}</dd>
      </div>
    </ReviewSection>
  );
}

function useIdlePatientSession(timeoutMs: number, active: boolean, onIdle: () => void) {
  const onIdleRef = useRef(onIdle);
  useEffect(() => {
    onIdleRef.current = onIdle;
  }, [onIdle]);
  useEffect(() => {
    if (!active || timeoutMs <= 0) return;
    let timer = window.setTimeout(() => onIdleRef.current(), timeoutMs);
    const resetTimer = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => onIdleRef.current(), timeoutMs);
    };
    const activityEvents = ["pointerdown", "keydown", "touchstart"] as const;
    activityEvents.forEach((eventName) => {
      window.addEventListener(eventName, resetTimer);
    });
    return () => {
      window.clearTimeout(timer);
      activityEvents.forEach((eventName) => {
        window.removeEventListener(eventName, resetTimer);
      });
    };
  }, [active, timeoutMs]);
}

/**
 * The visit's time on this device's clock, with the zone named ("EST"): an intake session does
 * not carry the clinic's time zone, so the time says which clock it is on.
 */
function formatScheduledAppointment(value: string): string {
  return new Intl.DateTimeFormat("en-CA", {
    weekday: "long",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
  }).format(new Date(value));
}

/**
 * The visit the questionnaire is for, on one line above the steps. A walk-in is already at the
 * clinic: no booked time, and no clinician named yet.
 */
function VisitLine({
  context,
  visitKind,
}: {
  context: ScheduledIntakeContext;
  visitKind: PublicVisitKind;
}) {
  return (
    <ul className="intake-visit scheduled-intake-summary" aria-label="Your visit">
      {visitKind === "walk_in" ? (
        <>
          <li>
            <Icon name="clock" />
            <span>Walk-in visit</span>
          </li>
          <li>
            <span>Today</span>
          </li>
        </>
      ) : (
        <>
          <li>
            <Icon name="calendar" />
            <span>{formatScheduledAppointment(context.startAt)}</span>
          </li>
          <li>
            <span>{context.appointmentTypeName}</span>
          </li>
          <li>
            <span>{context.providerDisplayName}</span>
          </li>
        </>
      )}
    </ul>
  );
}

function ScheduledCompletion({
  firstName,
  receipt,
  notice,
  context,
}: {
  firstName: string;
  receipt: string;
  notice: string | undefined;
  context: ScheduledIntakeContext;
}) {
  const headingRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    headingRef.current?.focus();
  }, []);
  return (
    <main className="completion scheduled-intake-completion" aria-labelledby="completion-heading">
      <span className="success-mark">
        <Icon name="check" />
      </span>
      <h1 id="completion-heading" ref={headingRef} tabIndex={-1}>
        {firstName === ""
          ? "Thanks. Your answers are sent."
          : `Thanks, ${firstName}. Your answers are sent.`}
      </h1>
      <p>Please check in with staff when you arrive. Submitting did not check you in.</p>
      <section className="scheduled-intake-ticket" aria-label="Appointment details">
        <div>
          <span>Appointment</span>
          <strong>{context.appointmentTypeName}</strong>
        </div>
        <div>
          <span>When</span>
          <strong>{formatScheduledAppointment(context.startAt)}</strong>
        </div>
        <div>
          <span>Clinician</span>
          <strong>{context.providerDisplayName}</strong>
        </div>
        <small>
          <Icon name="location" />
          {context.clinicAddress}
        </small>
      </section>
      <p className="completion-reference">
        <span>Completion reference</span> <strong>{receipt}</strong>
      </p>
      {notice ? <p className="completion-notice">{notice}</p> : null}
    </main>
  );
}

function SessionEnded({
  reason,
  onStart,
  clinicName,
}: {
  reason: SessionEndReason;
  onStart: () => void;
  clinicName: string;
}) {
  const headingRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    headingRef.current?.focus();
  }, []);
  return (
    <div className="patient-app">
      <PatientTopBar label="Secure questionnaire" clinicName={clinicName} />
      <main className="session-ended" aria-labelledby="session-ended-heading">
        <StateMark icon="lock" />
        <h1 id="session-ended-heading" ref={headingRef} tabIndex={-1}>
          Session ended
        </h1>
        <p>{sessionEndMessage(reason)}</p>
        <button type="button" className="continue-button" onClick={onStart}>
          Restart questionnaire
        </button>
      </main>
    </div>
  );
}

export function App({
  idleTimeoutMs = 10 * 60_000,
  initialIntake,
  lockedIdentity,
  scheduledContext,
  consentDocuments,
  questionnaireSections = FULL_PATIENT_INTAKE_SECTIONS,
  visitKind = "appointment",
  atClinic = visitKind === "walk_in",
  submitIntake,
  persisted = false,
  onSessionEnd,
  healthCardPhoto,
}: AppProps) {
  const queryClient = useQueryClient();
  // What the questionnaire starts from, and returns to when cleared: the booking's locked values
  // always win, so a read-only answer can only ever be the booking's.
  const startingIntake = useMemo<IntakeData>(
    () => ({ ...initialIntake, ...lockedIdentityValues(lockedIdentity) }),
    [initialIntake, lockedIdentity],
  );
  // The steps this link shows: its sections, then Review. The form starts at the first one.
  const visibleSteps = useMemo(() => stepsFor(questionnaireSections), [questionnaireSections]);
  const asksIdentity = patientIntakeAsksIdentity(questionnaireSections);
  const firstStep = visibleSteps[0]?.key ?? "review";
  const [step, setStep] = useState<QuestionnaireStepKey>(firstStep);
  const stepIndex = Math.max(
    0,
    visibleSteps.findIndex((candidate) => candidate.key === step),
  );
  const [intake, setIntake] = useState<IntakeData>(() => structuredClone(startingIntake));
  const [errors, setErrors] = useState<string[]>([]);
  const [consentChoices, setConsentChoices] = useState<ConsentChoices>(initialConsentChoices);
  const [completed, setCompleted] = useState(false);
  const [sessionEnded, setSessionEnded] = useState<SessionEndReason | null>(null);
  const sessionGenerationRef = useRef(0);
  const previousStepRef = useRef<QuestionnaireStepKey>(step);
  const resumeFocusRef = useRef(false);
  const errorSummaryRef = useRef<HTMLDivElement>(null);
  const clinicName = scheduledContext.clinicName;
  // The appointment's date of birth: the server refuses a questionnaire with any other.
  const verifiedDateOfBirth = initialIntake.dateOfBirth;
  const draftOptions = useMemo<PatientIntakeAnswersFromDraftOptions>(
    () => ({
      sections: questionnaireSections,
      ...(verifiedDateOfBirth === "" ? {} : { verifiedDateOfBirth }),
    }),
    [questionnaireSections, verifiedDateOfBirth],
  );
  const lockedFields = useMemo<LockedFields>(() => {
    const booked = lockedIdentityValues(lockedIdentity);
    return {
      ...(verifiedDateOfBirth === ""
        ? {}
        : {
            dateOfBirth: persisted
              ? "Verified when you opened this link. Wrong? Tell the front desk."
              : bookingIdentityNote,
          }),
      ...Object.fromEntries(Object.keys(booked).map((field) => [field, bookingIdentityNote])),
    };
  }, [lockedIdentity, persisted, verifiedDateOfBirth]);
  // The answers and errors as they are now, for a card reading that ends long after a render.
  const intakeRef = useRef(intake);
  const errorsRef = useRef(errors);
  useEffect(() => {
    intakeRef.current = intake;
    errorsRef.current = errors;
  }, [intake, errors]);
  // Reading the card on the phone: only for an Ontario card, and only while the patient is on
  // Coverage to see what it filled in. It fills only the coverage answers still empty when it
  // lands (a key typed meanwhile wins), nothing beside a typed number that is not the card's,
  // and never an identity answer.
  const applyCardReading = useCallback(
    (reading: HealthCardReading): HealthCardReadingApplied => {
      const fill = healthCardFill(intakeRef.current, reading);
      const filled = Object.keys(fill.values).length > 0;
      if (filled) {
        setIntake((current) => ({ ...current, ...healthCardFill(current, reading).values }));
        // Of the errors an earlier Continue showed, only those the filled answers fixed go.
        const shownErrors = errorsRef.current;
        const left = new Set(
          validateStep("coverage", { ...intakeRef.current, ...fill.values }, draftOptions),
        );
        const kept = shownErrors.filter((error) => left.has(error));
        const onSummary = errorSummaryRef.current?.contains(document.activeElement) === true;
        if (shownErrors.length > 0 && kept.length === 0) {
          // The summary goes: focus moves to the step's heading rather than to nowhere.
          if (onSummary) document.getElementById("intake-step-heading")?.focus();
          setErrors([]);
        } else if (kept.length < shownErrors.length && onSummary) {
          // Shortened only while the patient is on it: a new list takes focus to the summary.
          setErrors(kept);
        }
      }
      return {
        filled,
        numberDiffers: fill.numberDiffers,
        birthDateDiffers: fill.birthDateDiffers,
      };
    },
    [draftOptions],
  );
  const cardReading = useMemo<HealthCardReadingOptions>(
    () => ({
      ontarioCard: intake.coverageChoice === "ohip",
      onCoverage: step === "coverage",
      givenNumber: intake.healthNumber,
      givenBirthDate: intake.dateOfBirth,
      apply: applyCardReading,
    }),
    [intake.coverageChoice, intake.healthNumber, intake.dateOfBirth, step, applyCardReading],
  );
  // Held here, not in the coverage step, so Back and Continue keep the photo and its state.
  const cardPhoto = useHealthCardPhoto(healthCardPhoto, cardReading);
  const resetCardPhoto = cardPhoto.reset;
  const submission = useMutation({
    mutationFn: async ({
      answers,
      consent,
    }: {
      readonly answers: PatientIntakeAnswers;
      readonly consent: PublicIntakeConsent | undefined;
    }) => {
      const generation = sessionGenerationRef.current;
      return { generation, ...(await submitIntake(answers, consent)) };
    },
    onSuccess: (result) => {
      if (result.generation === sessionGenerationRef.current) setCompleted(true);
    },
    onError: (error) => {
      setErrors([
        // A request that fails its schema check would otherwise show the raw issue list.
        isSchemaError(error)
          ? answersNotAccepted
          : error instanceof Error
            ? error.message
            : "The questionnaire could not be submitted",
      ]);
      scrollToTop();
    },
  });
  const resetSubmission = submission.reset;
  const update: UpdateIntake = (key, next) => {
    if (lockedFields[key as LockedField] !== undefined) return;
    setIntake((current) => ({ ...current, [key]: next }));
    if (errors.length) setErrors([]);
  };
  const showErrors = (nextErrors: string[]) => {
    setErrors(nextErrors);
    scrollToTop();
  };
  /** To another step: an earlier step's problems are not this one's, so they go. */
  const goTo = (key: QuestionnaireStepKey) => {
    setStep(key);
    setErrors([]);
  };
  const next = () => {
    const nextErrors = validateStep(step, intake, draftOptions);
    if (nextErrors.length) {
      showErrors(nextErrors);
      return;
    }
    if (step !== "review") {
      goTo(visibleSteps[stepIndex + 1]?.key ?? "review");
      scrollToTop();
      return;
    }
    // The shared contract compacts the asked sections of the form into the answers the server
    // checks again; a section the link did not ask is left out whatever its fields hold.
    const answers = patientIntakeAnswersFromDraft(intake, draftOptions);
    if (!answers.success) {
      showErrors(submissionErrors(answers.issues));
      return;
    }
    if (consentDocuments !== undefined && !termsAgreed(consentDocuments, consentChoices)) {
      showErrors([termsRequiredToSubmit]);
      return;
    }
    submission.mutate({
      answers: answers.data,
      consent:
        consentDocuments === undefined
          ? undefined
          : intakeConsent(consentDocuments, consentChoices),
    });
  };
  const previousStep = visibleSteps[stepIndex - 1];
  const back = () => {
    if (previousStep !== undefined) goTo(previousStep.key);
  };
  const clearSensitiveState = useCallback(() => {
    sessionGenerationRef.current += 1;
    setIntake(structuredClone(startingIntake));
    setConsentChoices(initialConsentChoices);
    setErrors([]);
    setCompleted(false);
    setStep(firstStep);
    resetSubmission();
    resetCardPhoto();
    // Clears queries and mutations, including a submission that is still in flight.
    queryClient.clear();
  }, [startingIntake, firstStep, queryClient, resetSubmission, resetCardPhoto]);
  const endSession = useCallback(
    (reason: SessionEndReason) => {
      clearSensitiveState();
      setSessionEnded(reason);
      onSessionEnd?.(reason);
    },
    [clearSensitiveState, onSessionEnd],
  );
  const startNewSession = useCallback(() => {
    clearSensitiveState();
    setSessionEnded(null);
  }, [clearSensitiveState]);

  useIdlePatientSession(idleTimeoutMs, sessionEnded === null, () => endSession("idle"));
  useEffect(() => {
    const closeRestoredSession = (event: PageTransitionEvent) => {
      if (event.persisted) endSession("restored");
    };
    window.addEventListener("pageshow", closeRestoredSession);
    return () => window.removeEventListener("pageshow", closeRestoredSession);
  }, [endSession]);
  useEffect(() => {
    if (sessionEnded) {
      resumeFocusRef.current = true;
      return;
    }
    if (completed) return;
    if (previousStepRef.current !== step || resumeFocusRef.current)
      document.getElementById("intake-step-heading")?.focus();
    previousStepRef.current = step;
    resumeFocusRef.current = false;
  }, [completed, sessionEnded, step]);
  useEffect(() => {
    if (errors.length) errorSummaryRef.current?.focus();
  }, [errors]);

  if (sessionEnded)
    return <SessionEnded reason={sessionEnded} onStart={startNewSession} clinicName={clinicName} />;

  if (completed && submission.data)
    return (
      <div className="patient-app">
        <PatientTopBar label="Secure questionnaire" clinicName={clinicName} />
        <ScheduledCompletion
          firstName={intake.preferredName || intake.legalFirstName}
          receipt={submission.data.receipt}
          notice={submission.data.notice}
          context={scheduledContext}
        />
      </div>
    );

  return (
    <div className="patient-app">
      <PatientTopBar
        label="Secure questionnaire"
        clinicName={clinicName}
        onEndSession={() => endSession("manual")}
      />
      <main className="intake-main">
        <header className="intake-intro">
          <p className="intake-kicker">
            {!persisted
              ? "Synthetic demo · Required new-patient questionnaire"
              : visitKind === "walk_in"
                ? "Visit questionnaire"
                : "Pre-visit questionnaire"}
          </p>
          <VisitLine context={scheduledContext} visitKind={visitKind} />
        </header>
        <StepProgress
          label="Questionnaire progress"
          steps={visibleSteps.map((candidate) => candidate.label)}
          current={stepIndex}
          onSelect={(index) => {
            const target = visibleSteps[index];
            if (target !== undefined && index < stepIndex) goTo(target.key);
          }}
        />
        {errors.length ? (
          <div ref={errorSummaryRef} className="error-summary" role="alert" tabIndex={-1}>
            <strong>Please check the following:</strong>
            <ul>
              {errors.map((error) => (
                <li key={error}>{error}</li>
              ))}
            </ul>
          </div>
        ) : null}
        <form
          className="step-card"
          autoComplete="off"
          noValidate
          onSubmit={(event) => {
            event.preventDefault();
            next();
          }}
        >
          {previousStep === undefined ? null : (
            <button type="button" className="step-back-link" onClick={back}>
              <Icon name="chevronLeft" />
              {backLabels[previousStep.key]}
            </button>
          )}
          {step === "identity" ? (
            <AboutStep value={intake} update={update} locked={lockedFields} />
          ) : null}
          {step === "coverage" ? (
            <CoverageStep value={intake} update={update} photo={cardPhoto.control} />
          ) : null}
          {step === "health" ? <HealthStep value={intake} update={update} /> : null}
          {step === "review" ? (
            <ReviewStep
              value={intake}
              asksIdentity={asksIdentity}
              atClinic={atClinic}
              edit={goTo}
              agreement={
                consentDocuments === undefined ? null : (
                  <ConsentAgreement
                    documents={consentDocuments}
                    choices={consentChoices}
                    onChange={(choices) => {
                      setConsentChoices(choices);
                      if (errors.length) setErrors([]);
                    }}
                  />
                )
              }
            />
          ) : null}
          <footer className="step-actions">
            <button type="submit" className="continue-button" disabled={submission.isPending}>
              {submission.isPending ? (
                <>
                  <span className="button-spinner" />
                  Submitting questionnaire…
                </>
              ) : step === "review" ? (
                <>
                  <Icon name="check" />
                  Submit questionnaire
                </>
              ) : (
                <>
                  Continue
                  <Icon name="arrow" />
                </>
              )}
            </button>
          </footer>
        </form>
        <p className="privacy-footer">
          <Icon name="lock" />
          {persisted
            ? "Answers are sent when you submit · Nothing is saved on this device"
            : "Synthetic demo workspace · no external transmission"}
        </p>
      </main>
    </div>
  );
}
